import React, { useEffect, useRef, useState } from 'react';
import { useScrollReveal } from '@/lib/useScrollReveal';
import clsx from 'clsx';

interface MemoryNode {
  id: string;
  x: number;
  y: number;
  type: 'task_progress' | 'conversation' | 'tool_result' | 'context' | 'user_preference';
  content: string;
}

const typeColors: Record<string, string> = {
  task_progress: '#4F46E5', // indigo
  conversation: '#10B981', // emerald
  tool_result: '#F59E0B', // amber
  context: '#64748B', // slate
  user_preference: '#EC4899', // pink
};

const typeLabels: Record<string, string> = {
  task_progress: 'Task Progress',
  conversation: 'Conversation',
  tool_result: 'Tool Result',
  context: 'Context',
  user_preference: 'User Preference',
};

// Pre-computed node positions (no overlap)
const nodePositions: MemoryNode[] = [
  { id: '1', x: 150, y: 120, type: 'task_progress', content: 'Completed Phase 2 of DB migration' },
  { id: '2', x: 350, y: 80, type: 'conversation', content: 'User asked about pricing plans' },
  { id: '3', x: 520, y: 150, type: 'tool_result', content: 'Auth module refactored successfully' },
  { id: '4', x: 200, y: 280, type: 'context', content: 'Multi-step task: user onboarding flow' },
  { id: '5', x: 420, y: 300, type: 'user_preference', content: 'User prefers dark mode UI' },
  { id: '6', x: 600, y: 280, type: 'task_progress', content: 'Implemented search feature' },
  { id: '7', x: 100, y: 400, type: 'conversation', content: 'Discussed Q3 roadmap priorities' },
  { id: '8', x: 300, y: 420, type: 'tool_result', content: 'Generated analytics report' },
  { id: '9', x: 500, y: 420, type: 'context', content: 'API rate limiting review' },
  { id: '10', x: 650, y: 180, type: 'user_preference', content: 'Prefers concise responses' },
];

// Pre-computed connections (node id pairs)
const connections: [string, string][] = [
  ['1', '4'],
  ['1', '6'],
  ['2', '5'],
  ['2', '10'],
  ['3', '6'],
  ['3', '8'],
  ['4', '7'],
  ['5', '9'],
  ['6', '9'],
  ['7', '8'],
  ['8', '9'],
];

// Generate floating offset for animation
const getFloatingOffset = (id: string, time: number): { dx: number; dy: number } => {
  const base = parseInt(id, 10) * 0.7;
  return {
    dx: Math.sin(time * 0.001 + base) * 3,
    dy: Math.cos(time * 0.0015 + base) * 3,
  };
};

export const MemoryVisualization: React.FC = () => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [time, setTime] = useState(0);
  const [pulsingNode, setPulsingNode] = useState<string>('1');
  const animationRef = useRef<number>();

  useEffect(() => {
    let lastTime = 0;
    const animate = (currentTime: number) => {
      if (currentTime - lastTime > 50) {
        setTime(currentTime);
        lastTime = currentTime;
      }
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);

    // Pulse cycling
    const pulseInterval = setInterval(() => {
      setPulsingNode((prev) => {
        const currentIndex = nodePositions.findIndex((n) => n.id === prev);
        return nodePositions[(currentIndex + 1) % nodePositions.length].id;
      });
    }, 2000);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      clearInterval(pulseInterval);
    };
  }, []);

  return (
    <section className="py-20 bg-white" ref={ref}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={clsx(
            'text-center mb-12 transition-all duration-500',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          )}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
            Memory That Stays With You
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Every interaction stored, connected, and instantly retrievable
          </p>
        </div>

        <div
          className={clsx(
            'bg-surface-secondary rounded-2xl p-8 min-h-[400px] transition-all duration-500',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          )}
          style={{ transitionDelay: '150ms' }}
        >
          {/* Legend */}
          <div className="flex flex-wrap gap-4 mb-6 justify-center">
            {Object.entries(typeColors).map(([type, color]) => (
              <div key={type} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: color }}
                  aria-hidden="true"
                />
                <span className="text-xs text-text-secondary">
                  {typeLabels[type]}
                </span>
              </div>
            ))}
          </div>

          {/* SVG Canvas */}
          <svg
            viewBox="0 0 800 500"
            className="w-full h-auto"
            role="img"
            aria-label="Memory network visualization showing interconnected memory nodes"
          >
            <defs>
              {/* Glow filter for pulsing node */}
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Connection lines */}
            {connections.map(([fromId, toId]) => {
              const fromNode = nodePositions.find((n) => n.id === fromId);
              const toNode = nodePositions.find((n) => n.id === toId);
              if (!fromNode || !toNode) return null;

              const fromOffset = getFloatingOffset(fromId, time);
              const toOffset = getFloatingOffset(toId, time);

              return (
                <line
                  key={`${fromId}-${toId}`}
                  x1={fromNode.x + fromOffset.dx}
                  y1={fromNode.y + fromOffset.dy}
                  x2={toNode.x + toOffset.dx}
                  y2={toNode.y + toOffset.dy}
                  stroke="#CBD5E1"
                  strokeWidth="1.5"
                  opacity="0.6"
                  className="transition-all duration-100"
                />
              );
            })}

            {/* Memory nodes */}
            {nodePositions.map((node) => {
              const offset = getFloatingOffset(node.id, time);
              const isHovered = hoveredNode === node.id;
              const isPulsing = pulsingNode === node.id && !isHovered;
              const color = typeColors[node.type];

              return (
                <g
                  key={node.id}
                  transform={`translate(${node.x + offset.dx}, ${node.y + offset.dy})`}
                  onMouseEnter={() => setHoveredNode(node.id)}
                  onMouseLeave={() => setHoveredNode(null)}
                  className="cursor-pointer"
                  role="button"
                  aria-label={`${typeLabels[node.type]}: ${node.content}`}
                  tabIndex={0}
                >
                  {/* Outer glow for pulsing */}
                  {isPulsing && (
                    <circle
                      r="28"
                      fill={color}
                      opacity="0.3"
                      className="animate-ping"
                    />
                  )}

                  {/* Main node circle */}
                  <circle
                    r={isHovered ? 26 : isPulsing ? 24 : 20}
                    fill={color}
                    opacity={isHovered ? 1 : 0.85}
                    filter={isHovered || isPulsing ? 'url(#glow)' : undefined}
                    className="transition-all duration-200"
                  />

                  {/* Inner white ring */}
                  <circle r="6" fill="white" opacity="0.9" />

                  {/* Tooltip on hover */}
                  {isHovered && (
                    <g transform="translate(-100, -60)">
                      <rect
                        x="0"
                        y="0"
                        width="200"
                        height="44"
                        rx="6"
                        fill="white"
                        stroke="#E2E8F0"
                        strokeWidth="1"
                        className="shadow-lg"
                      />
                      <text
                        x="10"
                        y="18"
                        fill="#475569"
                        fontSize="10"
                        fontWeight="600"
                      >
                        {typeLabels[node.type]}
                      </text>
                      <text
                        x="10"
                        y="34"
                        fill="#0F172A"
                        fontSize="11"
                        className="overflow-hidden text-ellipsis"
                      >
                        {node.content.length > 30
                          ? node.content.slice(0, 30) + '...'
                          : node.content}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    </section>
  );
};

export default MemoryVisualization;